import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Tooltip,
  Popover
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Add as AddIcon, Remove as RemoveIcon, Delete as DeleteIcon, Edit as EditIcon, Cancel as CancelIcon, Archive as ArchiveIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { uk } from 'date-fns/locale';
import { bookingAPI, inventoryAPI, userAPI } from '../services/api';

// Custom function to format date with nominative case and capitalized month name
const formatDateWithMonth = (date) => {
  // Use 'LLL' for standalone abbreviated month name (nominative case)
  const formattedDate = format(date, 'd LLL yyyy HH:mm', { locale: uk });
  // Split the string to capitalize the month name
  const parts = formattedDate.split(' ');
  if (parts.length >= 2) {
    parts[1] = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
  }
  return parts.join(' ');
};

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // User info tooltip states
  const [userInfoAnchorEl, setUserInfoAnchorEl] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);

  // Delete booking states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);

  // Archive booking states
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [bookingToArchive, setBookingToArchive] = useState(null);

  // Status change states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [bookingToChangeStatus, setBookingToChangeStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Pickup status change states
  const [pickupDialogOpen, setPickupDialogOpen] = useState(false);
  const [bookingToChangePickup, setBookingToChangePickup] = useState(null);
  const [newPickupStatus, setNewPickupStatus] = useState(false);
  const [pickupChangeLoading, setPickupChangeLoading] = useState(false);

  // Return status change states
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [bookingToChangeReturn, setBookingToChangeReturn] = useState(null);
  const [newReturnStatus, setNewReturnStatus] = useState(false);
  const [returnChangeLoading, setReturnChangeLoading] = useState(false);

  // Edit booking states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    pickupDatetime: new Date(),
    returnDatetime: new Date(),
    purpose: '',
    additionalNotes: '',
    alternativePickupPerson: '',
    inventoryItems: []
  });
  const [availableItems, setAvailableItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [itemDetails, setItemDetails] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await bookingAPI.getAllBookings();
      setBookings(response.data);
      setError('');
    } catch (err) {
      setError('Не вдалося завантажити бронювання. Будь ласка, спробуйте пізніше.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // User info tooltip functions
  const handleUserInfoOpen = (event, userId) => {
    setUserInfoAnchorEl(event.currentTarget);
    fetchUserInfo(userId);
  };

  const handleUserInfoClose = () => {
    setUserInfoAnchorEl(null);
    setUserInfo(null);
  };

  const fetchUserInfo = async (userId) => {
    if (!userId) return;

    setUserInfoLoading(true);
    try {
      const response = await userAPI.getUserById(userId);
      setUserInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch user information', err);
    } finally {
      setUserInfoLoading(false);
    }
  };

  // Delete booking functions
  const handleDeleteClick = (booking) => {
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;

    try {
      await bookingAPI.deleteBooking(bookingToDelete.id);

      // Update the bookings list
      setBookings(bookings.filter(b => b.id !== bookingToDelete.id));
      setSnackbarMessage('Бронювання успішно видалено');
      setSnackbarOpen(true);
    } catch (err) {
      setError('Не вдалося видалити бронювання. Будь ласка, спробуйте ще раз.');
      console.error(err);
    } finally {
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  // Archive booking functions
  const handleArchiveClick = (booking) => {
    setBookingToArchive(booking);
    setArchiveDialogOpen(true);
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
    setBookingToArchive(null);
  };

  const handleArchiveConfirm = async () => {
    if (!bookingToArchive) return;

    try {
      await bookingAPI.archiveBooking(bookingToArchive.id);

      // Update the bookings list
      setBookings(bookings.filter(b => b.id !== bookingToArchive.id));
      setSnackbarMessage('Бронювання успішно архівовано');
      setSnackbarOpen(true);
    } catch (err) {
      setError('Не вдалося архівувати бронювання. Будь ласка, спробуйте ще раз.');
      console.error(err);
    } finally {
      setArchiveDialogOpen(false);
      setBookingToArchive(null);
    }
  };

  // Status change functions
  const handleStatusClick = (booking, status) => {
    setBookingToChangeStatus(booking);
    setNewStatus(status);
    setStatusDialogOpen(true);
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen(false);
    setBookingToChangeStatus(null);
    setNewStatus('');
  };

  const handleStatusConfirm = async () => {
    if (!bookingToChangeStatus || !newStatus) return;

    setStatusChangeLoading(true);
    try {
      const response = await bookingAPI.updateBookingStatus(bookingToChangeStatus.id, newStatus);

      // Update the booking in the local state
      const updatedBookings = bookings.map(booking => 
        booking.id === bookingToChangeStatus.id 
          ? { ...booking, status: newStatus }
          : booking
      );

      setBookings(updatedBookings);
      setSnackbarMessage(newStatus === 'APPROVED' ? 'Бронювання успішно підтверджено' : 'Бронювання успішно відхилено');
      setSnackbarOpen(true);
    } catch (err) {
      setError(newStatus === 'APPROVED' ? 'Не вдалося підтвердити бронювання. Будь ласка, спробуйте ще раз.' : 'Не вдалося відхилити бронювання. Будь ласка, спробуйте ще раз.');
      console.error(err);
    } finally {
      setStatusChangeLoading(false);
      setStatusDialogOpen(false);
      setBookingToChangeStatus(null);
      setNewStatus('');
    }
  };

  // Pickup status change functions
  const handlePickupClick = (booking, isPickedUp) => {
    setBookingToChangePickup(booking);
    setNewPickupStatus(isPickedUp);
    setPickupDialogOpen(true);
  };

  const handlePickupCancel = () => {
    setPickupDialogOpen(false);
    setBookingToChangePickup(null);
    setNewPickupStatus(false);
  };

  const handlePickupConfirm = async () => {
    if (!bookingToChangePickup) return;

    setPickupChangeLoading(true);
    try {
      await bookingAPI.updatePickupStatus(bookingToChangePickup.id, newPickupStatus);

      // Update the booking in the local state
      const updatedBookings = bookings.map(booking => {
        if (booking.id === bookingToChangePickup.id) {
          // If marking as not picked up, also set isReturned to false
          // to match the backend behavior
          if (!newPickupStatus) {
            return { ...booking, isPickedUp: false, isReturned: false };
          } else {
            return { ...booking, isPickedUp: true };
          }
        }
        return booking;
      });

      setBookings(updatedBookings);
      setSnackbarMessage(newPickupStatus ? 'Бронювання успішно позначено як отримане' : 'Бронювання успішно позначено як не отримане');
      setSnackbarOpen(true);
    } catch (err) {
      setError('Не вдалося оновити статус отримання. Будь ласка, спробуйте ще раз.');
      console.error(err);
    } finally {
      setPickupChangeLoading(false);
      setPickupDialogOpen(false);
      setBookingToChangePickup(null);
      setNewPickupStatus(false);
    }
  };

  // Return status change functions
  const handleReturnClick = (booking, isReturned) => {
    setBookingToChangeReturn(booking);
    setNewReturnStatus(isReturned);
    setReturnDialogOpen(true);
  };

  const handleReturnCancel = () => {
    setReturnDialogOpen(false);
    setBookingToChangeReturn(null);
    setNewReturnStatus(false);
  };

  const handleReturnConfirm = async () => {
    if (!bookingToChangeReturn) return;

    setReturnChangeLoading(true);
    try {
      await bookingAPI.updateReturnStatus(bookingToChangeReturn.id, newReturnStatus);

      // Update the booking in the local state
      const updatedBookings = bookings.map(booking => 
        booking.id === bookingToChangeReturn.id 
          ? { ...booking, isReturned: newReturnStatus }
          : booking
      );

      setBookings(updatedBookings);
      setSnackbarMessage(newReturnStatus ? 'Бронювання успішно позначено як повернуте' : 'Бронювання успішно позначено як не повернуте');
      setSnackbarOpen(true);
    } catch (err) {
      setError('Не вдалося оновити статус повернення. Будь ласка, спробуйте ще раз.');
      console.error(err);
    } finally {
      setReturnChangeLoading(false);
      setReturnDialogOpen(false);
      setBookingToChangeReturn(null);
      setNewReturnStatus(false);
    }
  };

  // Edit booking functions
  const fetchAvailableItems = useCallback(async () => {
    if (!editFormData.pickupDatetime || !editFormData.returnDatetime) return;

    setItemsLoading(true);
    try {
      const response = await inventoryAPI.getAvailableItems(
        format(editFormData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
        format(editFormData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss")
      );
      setAvailableItems(response.data);
    } catch (err) {
      console.error('Failed to fetch available items', err);
      setError('Не вдалося завантажити доступні предмети. Будь ласка, спробуйте ще раз.');
    } finally {
      setItemsLoading(false);
    }
  }, [editFormData.pickupDatetime, editFormData.returnDatetime]);

  const handleEditClick = (booking) => {
    setBookingToEdit(booking);

    // Convert string dates to Date objects
    const pickupDate = parseISO(booking.pickupDatetime);
    const returnDate = parseISO(booking.returnDatetime);

    // Map inventory items to the format expected by the form
    const inventoryItems = booking.inventoryItems.map(item => ({
      inventoryItemId: item.inventoryItemId,
      quantity: item.quantity
    }));

    // Initialize item details
    const details = {};
    booking.inventoryItems.forEach(item => {
      details[item.inventoryItemId] = {
        id: item.inventoryItemId,
        name: item.inventoryItemName
      };
    });

    setItemDetails(details);

    setEditFormData({
      pickupDatetime: pickupDate,
      returnDatetime: returnDate,
      purpose: booking.purpose,
      additionalNotes: booking.additionalNotes || '',
      alternativePickupPerson: booking.alternativePickupPerson || '',
      inventoryItems: inventoryItems
    });

    setEditDialogOpen(true);

    // Fetch available items for the selected time range
    fetchAvailableItems();
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setBookingToEdit(null);
    setEditFormData({
      pickupDatetime: new Date(),
      returnDatetime: new Date(),
      purpose: '',
      additionalNotes: '',
      alternativePickupPerson: '',
      inventoryItems: []
    });
    setSelectedItemId('');
    setSelectedQuantity(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (name, value) => {
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddItem = () => {
    if (!selectedItemId || selectedQuantity < 1) return;

    // Check if item is already in the list
    const existingItemIndex = editFormData.inventoryItems.findIndex(
      item => item.inventoryItemId === parseInt(selectedItemId)
    );

    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      const updatedItems = [...editFormData.inventoryItems];
      updatedItems[existingItemIndex].quantity += parseInt(selectedQuantity);

      setEditFormData(prev => ({
        ...prev,
        inventoryItems: updatedItems
      }));
    } else {
      // Add new item
      setEditFormData(prev => ({
        ...prev,
        inventoryItems: [
          ...prev.inventoryItems,
          {
            inventoryItemId: parseInt(selectedItemId),
            quantity: parseInt(selectedQuantity)
          }
        ]
      }));

      // Fetch item details if not already in state
      if (!itemDetails[selectedItemId]) {
        inventoryAPI.getItemById(selectedItemId)
          .then(response => {
            setItemDetails(prev => ({
              ...prev,
              [selectedItemId]: response.data
            }));
          })
          .catch(err => console.error(`Failed to fetch details for item ${selectedItemId}`, err));
      }
    }

    // Reset selection
    setSelectedItemId('');
    setSelectedQuantity(1);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = [...editFormData.inventoryItems];
    updatedItems.splice(index, 1);

    setEditFormData(prev => ({
      ...prev,
      inventoryItems: updatedItems
    }));
  };

  const handleIncreaseQuantity = (index) => {
    const updatedItems = [...editFormData.inventoryItems];
    const item = updatedItems[index];
    const inventoryItemId = item.inventoryItemId;

    // Find the available item to check maximum quantity
    const availableItem = availableItems.find(ai => ai.id === inventoryItemId);

    if (availableItem) {
      // Calculate how many more can be added
      const currentlyBooked = editFormData.inventoryItems.reduce((total, i) => {
        if (i.inventoryItemId === inventoryItemId) {
          return total + i.quantity;
        }
        return total;
      }, 0);

      // Only increase if not exceeding available quantity
      if (currentlyBooked < availableItem.availableQuantity) {
        updatedItems[index].quantity += 1;

        setEditFormData(prev => ({
          ...prev,
          inventoryItems: updatedItems
        }));
      }
    }
  };

  const handleDecreaseQuantity = (index) => {
    const updatedItems = [...editFormData.inventoryItems];

    // Only decrease if quantity is greater than 1
    if (updatedItems[index].quantity > 1) {
      updatedItems[index].quantity -= 1;

      setEditFormData(prev => ({
        ...prev,
        inventoryItems: updatedItems
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (editFormData.inventoryItems.length === 0) {
      setError('Будь ласка, додайте хоча б один предмет до бронювання.');
      return;
    }

    if (!editFormData.purpose) {
      setError('Будь ласка, вкажіть мету бронювання.');
      return;
    }

    if (editFormData.pickupDatetime >= editFormData.returnDatetime) {
      setError('Час отримання має бути раніше часу повернення.');
      return;
    }

    setEditLoading(true);
    setError('');

    try {
      await bookingAPI.updateBooking(bookingToEdit.id, {
        ...editFormData,
        pickupDatetime: format(editFormData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
        returnDatetime: format(editFormData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss")
      });

      // Update the booking in the local state
      const updatedBookings = bookings.map(booking => 
        booking.id === bookingToEdit.id 
          ? {
              ...booking,
              pickupDatetime: format(editFormData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
              returnDatetime: format(editFormData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
              purpose: editFormData.purpose,
              additionalNotes: editFormData.additionalNotes,
              alternativePickupPerson: editFormData.alternativePickupPerson,
              inventoryItems: editFormData.inventoryItems.map(item => ({
                id: item.id,
                inventoryItemId: item.inventoryItemId,
                inventoryItemName: itemDetails[item.inventoryItemId]?.name || `Item #${item.inventoryItemId}`,
                quantity: item.quantity
              }))
            }
          : booking
      );

      setBookings(updatedBookings);
      setSnackbarMessage('Бронювання успішно оновлено');
      setSnackbarOpen(true);
      setEditDialogOpen(false);
      setBookingToEdit(null);
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Не вдалося оновити бронювання. Будь ласка, перевірте інформацію та спробуйте ще раз.'
      );
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'DELETED':
        return 'default';
      case 'PENDING':
      default:
        return 'warning';
    }
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return formatDateWithMonth(new Date(dateTimeStr));
    } catch (e) {
      return dateTimeStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Панель адміна
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}


        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>ПІБ</TableCell>
                <TableCell>Альтернативна особа</TableCell>
                <TableCell>Отримання</TableCell>
                <TableCell>Повернення</TableCell>
                <TableCell>Мета використання</TableCell>
                <TableCell>Інвентар</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Хто підтвердив</TableCell>
                <TableCell>Отримано</TableCell>
                <TableCell>Хто підтвердив отримання</TableCell>
                <TableCell>Повернуто</TableCell>
                <TableCell>Хто підтвердив повернення</TableCell>
                <TableCell>Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.length > 0 ? (
                bookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>{booking.id}</TableCell>
                    <TableCell>
                      <Box 
                        component="span" 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.main'
                          }
                        }}
                        onMouseEnter={(e) => handleUserInfoOpen(e, booking.userId)}
                        onMouseLeave={handleUserInfoClose}
                      >
                        {booking.userFullName}
                      </Box>
                    </TableCell>
                    <TableCell>{booking.alternativePickupPerson || '-'}</TableCell>
                    <TableCell>{formatDateTime(booking.pickupDatetime)}</TableCell>
                    <TableCell>{formatDateTime(booking.returnDatetime)}</TableCell>
                    <TableCell>{booking.purpose}</TableCell>
                    <TableCell>
                      {booking.inventoryItems.map((item, index) => (
                        <div key={index}>
                          {item.inventoryItemName} (x{item.quantity})
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={booking.status} 
                        color={getStatusColor(booking.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {booking.approverFullName ? booking.approverFullName : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={booking.isPickedUp ? "Так" : "Ні"} 
                        color={booking.isPickedUp ? "success" : "default"} 
                        size="small" 
                      />
                      {booking.status === 'APPROVED' && (
                        <Button 
                          variant="outlined" 
                          color={booking.isPickedUp ? "primary" : "success"} 
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={() => handlePickupClick(booking, !booking.isPickedUp)}
                          title={booking.isPickedUp ? "Позначити як не отримане" : "Позначити як отримане"}
                        >
                          {booking.isPickedUp ? "Скасувати" : "Отримано"}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {booking.pickupConfirmerFullName ? booking.pickupConfirmerFullName : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={booking.isReturned ? "Так" : "Ні"} 
                        color={booking.isReturned ? "success" : "default"} 
                        size="small" 
                      />
                      {booking.status === 'APPROVED' && booking.isPickedUp && (
                        <Button 
                          variant="outlined" 
                          color={booking.isReturned ? "primary" : "success"} 
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={() => handleReturnClick(booking, !booking.isReturned)}
                          title={booking.isReturned ? "Позначити як не повернуте" : "Позначити як повернуте"}
                        >
                          {booking.isReturned ? "Скасувати" : "Повернуто"}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {booking.returnConfirmerFullName ? booking.returnConfirmerFullName : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {booking.status === 'PENDING' && (
                          <>
                            <Button 
                              variant="contained" 
                              color="success" 
                              size="small"
                              onClick={() => handleStatusClick(booking, 'APPROVED')}
                              title="Підтвердити бронювання"
                            >
                              Підтвердити
                            </Button>
                            <Button 
                              variant="contained" 
                              color="error" 
                              size="small"
                              onClick={() => handleStatusClick(booking, 'REJECTED')}
                              title="Відхилити бронювання"
                            >
                              Відхилити
                            </Button>
                          </>
                        )}
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleEditClick(booking)}
                          title="Редагувати бронювання"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleArchiveClick(booking)}
                          title="Архівувати бронювання"
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleDeleteClick(booking)}
                          title="Видалити бронювання"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    Нема бронювань
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Видалення бронювання</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете видалити це бронювання? Цю дію неможливо скасувати. Бронювання буде повністю видалено з бази даних.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Ні, залишити</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Так, видалити бронювання
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
      >
        <DialogTitle>Архівування бронювання</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете архівувати це бронювання? Бронювання буде переміщено в архів і не буде відображатися в основному списку.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel}>Ні, залишити</Button>
          <Button onClick={handleArchiveConfirm} color="primary" autoFocus>
            Так, архівувати бронювання
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusCancel}
      >
        <DialogTitle>
          {newStatus === 'APPROVED' ? 'Підтвердження бронювання' : 'Відхилення бронювання'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {newStatus === 'APPROVED' 
              ? 'Ви впевнені, що хочете підтвердити це бронювання?' 
              : 'Ви впевнені, що хочете відхилити це бронювання?'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusCancel}>Скасувати</Button>
          <Button 
            onClick={handleStatusConfirm} 
            color={newStatus === 'APPROVED' ? 'success' : 'error'} 
            autoFocus
            disabled={statusChangeLoading}
          >
            {statusChangeLoading 
              ? 'Обробка...' 
              : newStatus === 'APPROVED' 
                ? 'Так, підтвердити' 
                : 'Так, відхилити'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pickup Status Change Confirmation Dialog */}
      <Dialog
        open={pickupDialogOpen}
        onClose={handlePickupCancel}
      >
        <DialogTitle>
          {newPickupStatus ? 'Позначити як отримане' : 'Позначити як не отримане'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {newPickupStatus 
              ? 'Ви впевнені, що хочете позначити це бронювання як отримане?' 
              : 'Ви впевнені, що хочете позначити це бронювання як не отримане?'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePickupCancel}>Скасувати</Button>
          <Button 
            onClick={handlePickupConfirm} 
            color={newPickupStatus ? 'success' : 'error'} 
            autoFocus
            disabled={pickupChangeLoading}
          >
            {pickupChangeLoading 
              ? 'Обробка...' 
              : newPickupStatus 
                ? 'Так, позначити як отримане' 
                : 'Так, позначити як не отримане'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Status Change Confirmation Dialog */}
      <Dialog
        open={returnDialogOpen}
        onClose={handleReturnCancel}
      >
        <DialogTitle>
          {newReturnStatus ? 'Позначити як повернуте' : 'Позначити як не повернуте'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {newReturnStatus 
              ? 'Ви впевнені, що хочете позначити це бронювання як повернуте?' 
              : 'Ви впевнені, що хочете позначити це бронювання як не повернуте?'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReturnCancel}>Скасувати</Button>
          <Button 
            onClick={handleReturnConfirm} 
            color={newReturnStatus ? 'success' : 'error'} 
            autoFocus
            disabled={returnChangeLoading}
          >
            {returnChangeLoading 
              ? 'Обробка...' 
              : newReturnStatus 
                ? 'Так, позначити як повернуте' 
                : 'Так, позначити як не повернуте'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Редагування бронювання</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleEditSubmit} noValidate sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Дата і час отримання"
                    value={editFormData.pickupDatetime}
                    onChange={(newValue) => handleDateChange('pickupDatetime', newValue)}
                    sx={{ width: '100%' }}
                    format="dd MMM yyyy HH:mm"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Дата і час повернення"
                    value={editFormData.returnDatetime}
                    onChange={(newValue) => handleDateChange('returnDatetime', newValue)}
                    sx={{ width: '100%' }}
                    format="dd MMM yyyy HH:mm"
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            <TextField
              margin="normal"
              required
              fullWidth
              id="purpose"
              label="Мета бронювання"
              name="purpose"
              value={editFormData.purpose}
              onChange={handleInputChange}
            />

            <TextField
              margin="normal"
              fullWidth
              id="additionalNotes"
              label="Додаткові примітки"
              name="additionalNotes"
              multiline
              rows={3}
              value={editFormData.additionalNotes}
              onChange={handleInputChange}
            />

            <TextField
              margin="normal"
              fullWidth
              id="alternativePickupPerson"
              label="Альтернативна особа для отримання"
              name="alternativePickupPerson"
              value={editFormData.alternativePickupPerson || ''}
              onChange={handleInputChange}
              helperText="Якщо хтось інший буде забирати предмети, введіть їхнє ім'я тут"
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Вибрані предмети
            </Typography>

            <List>
              {editFormData.inventoryItems.map((item, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={itemDetails[item.inventoryItemId]?.name || `Предмет #${item.inventoryItemId}`}
                    secondary={`Кількість: ${item.quantity}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleDecreaseQuantity(index)}>
                      <RemoveIcon />
                    </IconButton>
                    <IconButton onClick={() => handleIncreaseQuantity(index)}>
                      <AddIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleRemoveItem(index)}>
                      <DeleteIcon color="primary" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {editFormData.inventoryItems.length === 0 && (
                <ListItem>
                  <ListItemText primary="Немає вибраних предметів" />
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Додати предмети
            </Typography>

            {itemsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Виберіть предмет"
                    value={selectedItemId}
                    onChange={(e) => setSelectedItemId(e.target.value)}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value=""></option>
                    {availableItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} (Доступно: {item.availableQuantity})
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Кількість"
                    type="number"
                    InputProps={{ inputProps: { min: 1 } }}
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    disabled={!selectedItemId}
                    fullWidth
                  >
                    Додати
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Скасувати</Button>
          <Button 
            onClick={handleEditSubmit} 
            color="primary" 
            disabled={editLoading || editFormData.inventoryItems.length === 0}
          >
            {editLoading ? 'Оновлення...' : 'Оновити бронювання'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Info Popover */}
      <Popover
        open={Boolean(userInfoAnchorEl)}
        anchorEl={userInfoAnchorEl}
        onClose={handleUserInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ pointerEvents: 'none' }}
      >
        <Paper sx={{ p: 2, maxWidth: 300 }}>
          {userInfoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : userInfo ? (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {userInfo.lastName} {userInfo.firstName} {userInfo.middleName || ''}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {userInfo.email}
              </Typography>
              {userInfo.phone && (
                <Typography variant="body2">
                  <strong>Телефон:</strong> {userInfo.phone}
                </Typography>
              )}
              {userInfo.position && (
                <Typography variant="body2">
                  <strong>Посада:</strong> {userInfo.position}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2">Інформація недоступна</Typography>
          )}
        </Paper>
      </Popover>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default AdminDashboard;
